import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";

export const SITE_NAME = "Yodi Data Hub";

/** "Connexion | Yodi Data Hub"; the bare site name when there is no page title. */
export function pageTitle(page: string | null | undefined): string {
  const name = page?.trim();
  return name ? `${name} | ${SITE_NAME}` : SITE_NAME;
}

/** Sets the browser tab title while the calling component is shown. */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

type Words = { fr: string; en: string };

const HOME: Words = {
  fr: "Collecte vocale et textuelle en langues ouest-africaines",
  en: "Voice and text data collection in West African languages",
};

const PAGES: Record<string, Words> = {
  "/login": { fr: "Connexion", en: "Sign in" },
  "/signup": { fr: "Inscription", en: "Sign up" },
  "/forgot-password": { fr: "Mot de passe oublié", en: "Forgot password" },
  "/leaderboard": { fr: "Classement", en: "Leaderboard" },
  "/data-policy": { fr: "Politique de données", en: "Data policy" },
  "/complaints": { fr: "Réclamations", en: "Complaints" },
  "/become-admin": { fr: "Demande d'accès organisation", en: "Organisation access request" },
};

/**
 * The tab title of a route: "Yodi Data Hub | ..." on the home page (as in
 * index.html), "Page | Yodi Data Hub" on the other public pages, the bare
 * site name elsewhere so a title never lingers from the previous page.
 */
export function routeTitle(path: string, language: string): string {
  const rest = path.replace(/^\/[a-z]{2}(?=\/|$)/, "").replace(/\/+$/, "") || "/";
  const pick = (words: Words) => (language === "en" ? words.en : words.fr);
  if (rest === "/" || rest === "/site" || rest === "/dashboard") return `${SITE_NAME} | ${pick(HOME)}`;
  const page = PAGES[rest];
  return pageTitle(page ? pick(page) : null);
}

/**
 * Mounted once inside the localized routes: one title per public page,
 * without touching the pages themselves.
 */
export function RouteDocumentTitle() {
  const { pathname } = useLocation();
  const { lang = "fr" } = useParams<{ lang: string }>();
  useDocumentTitle(routeTitle(pathname, lang));
  return null;
}
